import {
  INTRANET_IMAGE_ACCEPT,
  SITE_DIARY_ACTIVITY_LABELS,
  SITE_DIARY_COLLEAGUES_HINT,
  SITE_DIARY_INCIDENT_HINT,
  SITE_DIARY_JOB_TITLE_HINT,
  SITE_DIARY_MAX_PHOTOS,
  SITE_DIARY_PHOTO_HINT,
  SITE_DIARY_TEMPERATURE_HINT,
  SITE_DIARY_WEATHER_LABELS,
  SiteDiaryActivity,
  SiteDiaryWeather,
  siteDiaryDeliveryMessage,
  siteDiaryDraftSchema,
  siteDiaryReportSchema,
  type IntranetModuleReport,
  type IntranetModuleReportPhoto,
  type PutIntranetModuleDraftRequest,
  type SiteDiaryActivity as SiteDiaryActivityValue,
  type SiteDiaryWeather as SiteDiaryWeatherValue,
  type SubmitIntranetModuleReportRequest,
} from '@gb-ticket/shared-types';
import { ArrowDownIcon, ArrowLeftIcon, ArrowUpIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { useId, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  intranetPanelClassName,
  intranetTableFrameClassName,
} from '@/products/intranet/features/intranet/intranet-panel';
import {
  useAddIntranetModulePhoto,
  useIntranetModule,
  useIntranetModuleReports,
  usePutIntranetModuleDraft,
  useRemoveIntranetModulePhoto,
  useReorderIntranetModulePhotos,
  useSubmitIntranetModuleReport,
} from '@/products/intranet/features/intranet/use-intranet-directory';
import { INTRANET_ROOT } from '@/products/intranet/intranet-paths';

const ACTIVITIES = Object.values(SiteDiaryActivity);
const WEATHER = Object.values(SiteDiaryWeather);

type FormState = {
  runningNumber: string;
  date: string;
  project: string;
  address: string;
  colleagues: string;
  activities: SiteDiaryActivityValue | '';
  activityOther: string;
  notes: string;
  sguNotes: string;
  incidents: string;
  extraEffort: boolean;
  extraEffortReason: string;
  weather: SiteDiaryWeatherValue | '';
  temperature: string;
};

function berlinTodayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
}

function emptyForm(): FormState {
  return {
    runningNumber: '',
    date: berlinTodayIso(),
    project: '',
    address: '',
    colleagues: '',
    activities: '',
    activityOther: '',
    notes: '',
    sguNotes: '',
    incidents: '',
    extraEffort: false,
    extraEffortReason: '',
    weather: '',
    temperature: '',
  };
}

function parsePayload(payload: unknown, fallbackRunningNumber?: string | null): FormState {
  const parsed = siteDiaryDraftSchema.safeParse(payload ?? {});
  if (!parsed.success) {
    return emptyForm();
  }
  const value = parsed.data;
  return {
    runningNumber:
      value.runningNumber.length > 0 ? value.runningNumber : (fallbackRunningNumber?.trim() ?? ''),
    date: value.date || berlinTodayIso(),
    project: value.project,
    address: value.address,
    colleagues: value.colleagues,
    activities: value.activities,
    activityOther: value.activityOther,
    notes: value.notes,
    sguNotes: value.sguNotes,
    incidents: value.incidents,
    extraEffort: value.extraEffort,
    extraEffortReason: value.extraEffortReason,
    weather: value.weather,
    temperature: value.temperature,
  };
}

function toDraftBody(form: FormState): PutIntranetModuleDraftRequest {
  return {
    runningNumber: form.runningNumber,
    date: form.date,
    project: form.project,
    address: form.address,
    colleagues: form.colleagues,
    activities: form.activities,
    activityOther: form.activityOther,
    notes: form.notes,
    sguNotes: form.sguNotes,
    incidents: form.incidents,
    extraEffort: form.extraEffort,
    extraEffortReason: form.extraEffort ? form.extraEffortReason : '',
    weather: form.weather,
    temperature: form.temperature,
  };
}

function toSubmitBody(form: FormState): SubmitIntranetModuleReportRequest | null {
  const body = {
    runningNumber: form.runningNumber,
    date: form.date,
    project: form.project,
    address: form.address,
    colleagues: form.colleagues,
    activities: form.activities,
    activityOther: form.activityOther,
    notes: form.notes,
    sguNotes: form.sguNotes,
    incidents: form.incidents,
    extraEffort: form.extraEffort,
    extraEffortReason: form.extraEffort ? form.extraEffortReason : '',
    weather: form.weather,
    temperature: form.temperature,
  };
  const parsed = siteDiaryReportSchema.safeParse(body);
  if (!parsed.success) {
    const first = parsed.error.issues[0]?.message ?? 'Bitte das Formular prüfen.';
    toast.error(first);
    return null;
  }
  return body;
}

function toastApiError(error: unknown, fallback: string) {
  toast.error(error instanceof ApiRequestError ? error.message : fallback);
}

function reportTitle(report: IntranetModuleReport): string {
  if (report.runningNumber) {
    return report.runningNumber;
  }
  const parsed = siteDiaryDraftSchema.safeParse(report.payload ?? {});
  const project = parsed.success ? parsed.data.project.trim() : '';
  if (report.status === 'draft') {
    return project ? `Entwurf – ${project}` : 'Entwurf';
  }
  return project || 'Bericht';
}

/** Bautagesbericht form on `/intranet/modul/:instanceId` (§6, §9.3). */
export function SiteDiaryPage() {
  const { instanceId = '' } = useParams();
  const formId = useId();
  const me = useMe();
  const instance = useIntranetModule(instanceId);
  const reports = useIntranetModuleReports(instanceId);
  const putDraft = usePutIntranetModuleDraft(instanceId);
  const submitReport = useSubmitIntranetModuleReport(instanceId);
  const addPhoto = useAddIntranetModulePhoto(instanceId);
  const removePhoto = useRemoveIntranetModulePhoto(instanceId);
  const reorderPhotos = useReorderIntranetModulePhotos(instanceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [viewReportId, setViewReportId] = useState<string | null>(null);
  const [submittedSnapshot, setSubmittedSnapshot] = useState<IntranetModuleReport | null>(null);
  const [hydratedFrom, setHydratedFrom] = useState<string | null>(null);

  const title = instance.data?.label ?? 'Modul';
  usePageTitle(title);

  const myUserId = me.data?.id;
  const items = (reports.data?.items ?? []).filter(
    (entry) => !myUserId || entry.authorId === myUserId,
  );
  const draftReport = items.find((entry) => entry.status === 'draft') ?? null;
  const viewReport =
    viewReportId == null
      ? null
      : submittedSnapshot?.id === viewReportId
        ? submittedSnapshot
        : (items.find((entry) => entry.id === viewReportId) ?? null);
  const readOnly = viewReport?.status === 'submitted';

  const draftHydrationKey =
    !readOnly && reports.isSuccess
      ? draftReport
        ? `${draftReport.id}:${draftReport.updatedAt}`
        : 'new'
      : null;
  if (draftHydrationKey != null && hydratedFrom !== draftHydrationKey) {
    setHydratedFrom(draftHydrationKey);
    setForm(draftReport ? parsePayload(draftReport.payload) : emptyForm());
  }

  const photos: IntranetModuleReportPhoto[] = readOnly
    ? (viewReport?.photos ?? [])
    : (draftReport?.photos ?? []);

  const busy =
    putDraft.isPending ||
    submitReport.isPending ||
    addPhoto.isPending ||
    removePhoto.isPending ||
    reorderPhotos.isPending;

  async function ensureDraftSaved(): Promise<void> {
    await putDraft.mutateAsync(toDraftBody(form));
  }

  async function onSaveDraft() {
    try {
      await ensureDraftSaved();
      toast.success('Entwurf gespeichert.');
      setViewReportId(null);
    } catch (error) {
      toastApiError(error, 'Entwurf speichern fehlgeschlagen.');
    }
  }

  async function onSubmit() {
    const body = toSubmitBody(form);
    if (!body) {
      return;
    }
    try {
      const result = await submitReport.mutateAsync(body);
      toast.success(
        siteDiaryDeliveryMessage({
          mode: 'submit',
          runningNumber: result.runningNumber,
          delivery: result.delivery,
          mailSent: result.mailSent,
          mailError: result.mailError,
          notified: result.notified,
          notifyError: result.notifyError,
        }),
      );
      setSubmittedSnapshot(result);
      setViewReportId(result.id);
      setHydratedFrom(null);
    } catch (error) {
      toastApiError(error, 'Absenden fehlgeschlagen.');
    }
  }

  async function onPhotosSelected(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) {
      return;
    }
    const remaining = SITE_DIARY_MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(`Höchstens ${SITE_DIARY_MAX_PHOTOS} Fotos sind erlaubt.`);
      return;
    }
    const files = Array.from(fileList).slice(0, remaining);
    try {
      if (!draftReport) {
        await ensureDraftSaved();
      }
      for (const file of files) {
        await addPhoto.mutateAsync(file);
      }
    } catch (error) {
      toastApiError(error, 'Foto hochladen fehlgeschlagen.');
    }
  }

  async function onRemovePhoto(photoId: string) {
    try {
      await removePhoto.mutateAsync(photoId);
    } catch (error) {
      toastApiError(error, 'Foto entfernen fehlgeschlagen.');
    }
  }

  async function onMovePhoto(photoId: string, direction: -1 | 1) {
    const ordered = [...photos].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = ordered.findIndex((photo) => photo.id === photoId);
    const swap = index + direction;
    if (index < 0 || swap < 0 || swap >= ordered.length) {
      return;
    }
    const next = [...ordered];
    const current = next[index]!;
    next[index] = next[swap]!;
    next[swap] = current;
    try {
      await reorderPhotos.mutateAsync({ photoIds: next.map((photo) => photo.id) });
    } catch (error) {
      toastApiError(error, 'Reihenfolge ändern fehlgeschlagen.');
    }
  }

  function openReport(report: IntranetModuleReport) {
    if (report.status === 'draft') {
      setSubmittedSnapshot(null);
      setViewReportId(null);
      setForm(parsePayload(report.payload));
      setHydratedFrom(`${report.id}:${report.updatedAt}`);
      return;
    }
    setSubmittedSnapshot(report);
    setViewReportId(report.id);
  }

  function startNewDraft() {
    setSubmittedSnapshot(null);
    setViewReportId(null);
    if (draftReport) {
      setForm(parsePayload(draftReport.payload));
      setHydratedFrom(`${draftReport.id}:${draftReport.updatedAt}`);
      return;
    }
    setForm(emptyForm());
    setHydratedFrom('new');
  }

  const displayForm = readOnly
    ? parsePayload(viewReport?.payload, viewReport?.runningNumber)
    : form;
  const logoSrc = instance.data?.kind === 'site_diary' ? instance.data.logoUrl : null;

  return (
    <section className="min-w-0 space-y-6">
      <PlatformPageHeader
        title={title}
        description="Bautagesbericht"
        action={
          <Button asChild variant="outline" size="sm" className="min-h-11">
            <Link to={INTRANET_ROOT}>
              <ArrowLeftIcon aria-hidden />
              Zurück
            </Link>
          </Button>
        }
      />

      {instance.isPending || reports.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Modul wird geladen …
        </p>
      ) : instance.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {instance.error instanceof ApiRequestError
            ? instance.error.message
            : 'Dieses Modul konnte nicht geladen werden.'}
        </p>
      ) : (
        <>
          {logoSrc ? (
            <div
              className={`${intranetPanelClassName} flex max-w-3xl items-center justify-start p-4`}
            >
              <img src={logoSrc} alt="" className="max-h-16 w-auto max-w-[220px] object-contain" />
            </div>
          ) : null}
          {readOnly ? (
            <ReadOnlyReport
              formId={formId}
              form={displayForm}
              photos={photos}
              pdfStorageKey={viewReport?.pdfStorageKey ?? null}
              instanceId={instanceId}
              reportId={viewReport.id}
              onEditDraft={draftReport ? startNewDraft : undefined}
            />
          ) : (
            <SiteDiaryForm
              formId={formId}
              form={form}
              setForm={setForm}
              photos={photos}
              busy={busy}
              onSaveDraft={() => void onSaveDraft()}
              onSubmit={() => void onSubmit()}
              onPhotosSelected={(files) => void onPhotosSelected(files)}
              onRemovePhoto={(id) => void onRemovePhoto(id)}
              onMovePhoto={(id, dir) => void onMovePhoto(id, dir)}
            />
          )}

          <MeineBerichte
            items={items}
            activeId={readOnly ? viewReportId : (draftReport?.id ?? null)}
            onOpen={openReport}
          />
        </>
      )}
    </section>
  );
}

function SiteDiaryForm({
  formId,
  form,
  setForm,
  photos,
  busy,
  onSaveDraft,
  onSubmit,
  onPhotosSelected,
  onRemovePhoto,
  onMovePhoto,
}: {
  formId: string;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  photos: IntranetModuleReportPhoto[];
  busy: boolean;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onPhotosSelected: (files: FileList | null) => void;
  onRemovePhoto: (photoId: string) => void;
  onMovePhoto: (photoId: string, direction: -1 | 1) => void;
}) {
  const orderedPhotos = [...photos].sort((a, b) => a.sortOrder - b.sortOrder);

  function patch(partial: Partial<FormState>) {
    setForm((previous) => ({ ...previous, ...partial }));
  }

  return (
    <form
      className={`${intranetPanelClassName} flex max-w-3xl flex-col gap-6`}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <fieldset className="space-y-4" disabled={busy}>
        <legend className="sr-only">Stammdaten</legend>
        <Field label="Laufende Nummer" htmlFor={`${formId}-number`} required>
          <Input
            id={`${formId}-number`}
            className="min-h-11"
            value={form.runningNumber}
            maxLength={80}
            onChange={(event) => patch({ runningNumber: event.target.value })}
            required
            placeholder="z. B. BT-2026-0001"
          />
        </Field>
        <Field label="Datum" htmlFor={`${formId}-date`} required>
          <Input
            id={`${formId}-date`}
            type="date"
            className="min-h-11"
            value={form.date}
            onChange={(event) => patch({ date: event.target.value })}
            required
          />
        </Field>
        <Field label="Bauprojekt" htmlFor={`${formId}-project`} required>
          <Input
            id={`${formId}-project`}
            className="min-h-11"
            value={form.project}
            maxLength={200}
            onChange={(event) => patch({ project: event.target.value })}
            required
          />
        </Field>
        <Field label="Adresse Kunde/Baustelle" htmlFor={`${formId}-address`} required>
          <Textarea
            id={`${formId}-address`}
            className="min-h-11"
            value={form.address}
            maxLength={500}
            onChange={(event) => patch({ address: event.target.value })}
            required
          />
        </Field>
      </fieldset>

      <fieldset className="space-y-2" disabled={busy}>
        <legend className="sr-only">Beteiligte Kollegen und Arbeitszeiten</legend>
        <Field
          label="Beteiligte Kollegen und Arbeitszeiten"
          htmlFor={`${formId}-colleagues`}
          required
        >
          <Textarea
            id={`${formId}-colleagues`}
            className="min-h-48 font-mono text-sm"
            value={form.colleagues}
            maxLength={8000}
            onChange={(event) => patch({ colleagues: event.target.value })}
            required
            placeholder={[
              'Gewerk: Lüftung/ Elektroarbeiten 666155700',
              '',
              'Obermonteur Steffen Kahnt: 11.15 bis 16.15 Uhr / 4,5 h',
              'Azubi Leonardo Engelhardt: 11.15 bis 16.15 Uhr / 4,5 h',
            ].join('\n')}
          />
          <p className="mt-1 text-sm text-muted-foreground">{SITE_DIARY_COLLEAGUES_HINT}</p>
          <p className="mt-1 text-sm text-muted-foreground">{SITE_DIARY_JOB_TITLE_HINT}</p>
        </Field>
      </fieldset>

      <fieldset className="space-y-2" disabled={busy}>
        <legend className="sr-only">Tätigkeit</legend>
        <Field label="Tätigkeit" htmlFor={`${formId}-activity`} required>
          <Select
            value={form.activities || 'none'}
            onValueChange={(value) => {
              const next = value === 'none' ? '' : (value as SiteDiaryActivityValue);
              patch({
                activities: next,
                ...(next !== SiteDiaryActivity.SONSTIGES ? { activityOther: '' } : {}),
              });
            }}
          >
            <SelectTrigger id={`${formId}-activity`} className="min-h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Tätigkeit wählen</SelectItem>
              {ACTIVITIES.map((activity) => (
                <SelectItem key={activity} value={activity}>
                  {SITE_DIARY_ACTIVITY_LABELS[activity]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {form.activities === SiteDiaryActivity.SONSTIGES ? (
          <Field label="Sonstiges" htmlFor={`${formId}-activity-other`} required>
            <Input
              id={`${formId}-activity-other`}
              className="min-h-11"
              value={form.activityOther}
              maxLength={200}
              onChange={(event) => patch({ activityOther: event.target.value })}
              required
            />
          </Field>
        ) : null}
      </fieldset>

      <fieldset className="space-y-4" disabled={busy}>
        <legend className="sr-only">Texte</legend>
        <Field label="Notizen" htmlFor={`${formId}-notes`}>
          <Textarea
            id={`${formId}-notes`}
            className="min-h-24"
            value={form.notes}
            maxLength={4000}
            onChange={(event) => patch({ notes: event.target.value })}
          />
        </Field>
        <Field
          label="SGU-Besonderheiten (Sicherheit, Gesundheit, Umweltschutz)"
          htmlFor={`${formId}-sgu`}
        >
          <Textarea
            id={`${formId}-sgu`}
            className="min-h-24"
            value={form.sguNotes}
            maxLength={4000}
            onChange={(event) => patch({ sguNotes: event.target.value })}
          />
        </Field>
        <Field label="Besondere Vorkommnisse" htmlFor={`${formId}-incidents`}>
          <Textarea
            id={`${formId}-incidents`}
            className="min-h-24"
            value={form.incidents}
            maxLength={4000}
            onChange={(event) => patch({ incidents: event.target.value })}
          />
          <p className="mt-1 text-sm text-muted-foreground">{SITE_DIARY_INCIDENT_HINT}</p>
        </Field>
      </fieldset>

      <fieldset className="space-y-3" disabled={busy}>
        <legend className="text-sm font-medium">Montagemehraufwand</legend>
        <div className="flex flex-wrap gap-4">
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="radio"
              name={`${formId}-extra`}
              checked={!form.extraEffort}
              onChange={() => patch({ extraEffort: false, extraEffortReason: '' })}
            />
            Nein
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="radio"
              name={`${formId}-extra`}
              checked={form.extraEffort}
              onChange={() => patch({ extraEffort: true })}
            />
            Ja
          </label>
        </div>
        {form.extraEffort ? (
          <Field
            label="Begründung für Montagemehraufwand"
            htmlFor={`${formId}-extra-reason`}
            required
          >
            <Textarea
              id={`${formId}-extra-reason`}
              className="min-h-24"
              value={form.extraEffortReason}
              maxLength={2000}
              onChange={(event) => patch({ extraEffortReason: event.target.value })}
              required
            />
          </Field>
        ) : null}
      </fieldset>

      <fieldset className="space-y-3" disabled={busy}>
        <legend className="sr-only">Wetter</legend>
        <Field label="Wetter" htmlFor={`${formId}-weather`} required>
          <Select
            value={form.weather || 'none'}
            onValueChange={(value) =>
              patch({ weather: value === 'none' ? '' : (value as SiteDiaryWeatherValue) })
            }
          >
            <SelectTrigger id={`${formId}-weather`} className="min-h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Wetter wählen</SelectItem>
              {WEATHER.map((entry) => (
                <SelectItem key={entry} value={entry}>
                  {SITE_DIARY_WEATHER_LABELS[entry]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Temperatur" htmlFor={`${formId}-temperature`} required>
          <Input
            id={`${formId}-temperature`}
            className="min-h-11 max-w-md"
            value={form.temperature}
            maxLength={80}
            onChange={(event) => patch({ temperature: event.target.value })}
            required
            placeholder="6°C bis 21°C"
          />
          <p className="mt-1 text-sm text-muted-foreground">{SITE_DIARY_TEMPERATURE_HINT}</p>
        </Field>
      </fieldset>

      <fieldset className="space-y-3" disabled={busy}>
        <legend className="text-sm font-medium">Fotodokumentation</legend>
        <Input
          type="file"
          accept={INTRANET_IMAGE_ACCEPT}
          multiple
          className="min-h-11"
          disabled={orderedPhotos.length >= SITE_DIARY_MAX_PHOTOS}
          onChange={(event) => {
            onPhotosSelected(event.target.files);
            event.target.value = '';
          }}
        />
        {orderedPhotos.length > 0 ? (
          <ul className="space-y-2">
            {orderedPhotos.map((photo, index) => (
              <li
                key={photo.id}
                className={`${intranetPanelClassName} flex flex-col gap-2 p-2 sm:flex-row sm:items-center`}
              >
                <img
                  src={photo.url}
                  alt={`Foto ${index + 1}`}
                  className="h-20 w-28 rounded object-cover"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11"
                    disabled={index === 0}
                    onClick={() => onMovePhoto(photo.id, -1)}
                    aria-label={`Foto ${index + 1} nach oben`}
                  >
                    <ArrowUpIcon aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11"
                    disabled={index === orderedPhotos.length - 1}
                    onClick={() => onMovePhoto(photo.id, 1)}
                    aria-label={`Foto ${index + 1} nach unten`}
                  >
                    <ArrowDownIcon aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="min-h-11"
                    onClick={() => onRemovePhoto(photo.id)}
                    aria-label={`Foto ${index + 1} entfernen`}
                  >
                    <Trash2Icon aria-hidden />
                    Entfernen
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="text-sm text-muted-foreground">{SITE_DIARY_PHOTO_HINT}</p>
      </fieldset>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          disabled={busy}
          onClick={onSaveDraft}
        >
          {busy ? <Loader2Icon className="size-4 animate-spin" aria-hidden /> : null}
          Entwurf speichern
        </Button>
        <Button type="submit" className="min-h-11" disabled={busy}>
          Bautagesbericht absenden
        </Button>
      </div>
    </form>
  );
}

function ReadOnlyReport({
  formId,
  form,
  photos,
  pdfStorageKey,
  instanceId,
  reportId,
  onEditDraft,
}: {
  formId: string;
  form: FormState;
  photos: IntranetModuleReportPhoto[];
  pdfStorageKey: string | null;
  instanceId: string;
  reportId: string;
  onEditDraft?: () => void;
}) {
  const orderedPhotos = [...photos].sort((a, b) => a.sortOrder - b.sortOrder);
  const pdfReady = Boolean(pdfStorageKey);

  return (
    <div className={`${intranetPanelClassName} flex max-w-3xl flex-col gap-6`}>
      <dl className="grid gap-4 text-sm">
        <ReadonlyField label="Laufende Nummer" value={form.runningNumber.trim() || '—'} />
        <ReadonlyField label="Datum" value={form.date || '—'} />
        <ReadonlyField label="Bauprojekt" value={form.project || '—'} />
        <ReadonlyField label="Adresse Kunde/Baustelle" value={form.address || '—'} />
        <div>
          <dt className="font-medium">Beteiligte Kollegen und Arbeitszeiten</dt>
          <dd className="mt-2 whitespace-pre-wrap">
            {form.colleagues.trim() ? form.colleagues : '—'}
          </dd>
        </div>
        <ReadonlyField
          label="Tätigkeit"
          value={
            form.activities
              ? [
                  SITE_DIARY_ACTIVITY_LABELS[form.activities],
                  form.activities === SiteDiaryActivity.SONSTIGES && form.activityOther
                    ? `(${form.activityOther})`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' ')
              : '—'
          }
        />
        <ReadonlyField label="Notizen" value={form.notes || '—'} />
        <ReadonlyField
          label="SGU-Besonderheiten (Sicherheit, Gesundheit, Umweltschutz)"
          value={form.sguNotes || '—'}
        />
        <div>
          <dt className="font-medium">Besondere Vorkommnisse</dt>
          <dd className="mt-1 whitespace-pre-wrap">{form.incidents || '—'}</dd>
          <p className="mt-1 text-muted-foreground">{SITE_DIARY_INCIDENT_HINT}</p>
        </div>
        <ReadonlyField
          label="Montagemehraufwand"
          value={form.extraEffort ? `Ja – ${form.extraEffortReason || '—'}` : 'Nein'}
        />
        <ReadonlyField
          label="Wetter"
          value={form.weather ? SITE_DIARY_WEATHER_LABELS[form.weather] : '—'}
        />
        <ReadonlyField
          label="Temperatur"
          value={form.temperature.trim() ? form.temperature : '—'}
        />
        <div>
          <dt className="font-medium">Fotodokumentation</dt>
          <dd className="mt-2 flex flex-wrap gap-2">
            {orderedPhotos.length === 0 ? (
              <span>—</span>
            ) : (
              orderedPhotos.map((photo, index) => (
                <img
                  key={photo.id}
                  src={photo.url}
                  alt={`Foto ${index + 1}`}
                  className="h-20 w-28 rounded object-cover"
                />
              ))
            )}
          </dd>
          <p className="mt-2 text-muted-foreground">{SITE_DIARY_PHOTO_HINT}</p>
        </div>
      </dl>

      <div className="flex flex-col gap-3 sm:flex-row">
        {pdfReady ? (
          <Button
            type="button"
            className="min-h-11"
            onClick={() => void downloadReportPdf(instanceId, reportId)}
          >
            PDF herunterladen
          </Button>
        ) : (
          <Button type="button" className="min-h-11" disabled>
            PDF folgt
          </Button>
        )}
        {onEditDraft ? (
          <Button type="button" variant="outline" className="min-h-11" onClick={onEditDraft}>
            Zum Entwurf
          </Button>
        ) : null}
      </div>
      <span id={formId} className="sr-only">
        Nur-lesbare Ansicht
      </span>
    </div>
  );
}

function MeineBerichte({
  items,
  activeId,
  onOpen,
}: {
  items: IntranetModuleReport[];
  activeId: string | null;
  onOpen: (report: IntranetModuleReport) => void;
}) {
  return (
    <section className="max-w-3xl space-y-3" aria-labelledby="meine-berichte-heading">
      <h2 id="meine-berichte-heading" className="text-base font-semibold">
        Meine Berichte
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Berichte.</p>
      ) : (
        <ul className={`${intranetTableFrameClassName} divide-y`}>
          {items.map((report) => {
            const active = report.id === activeId;
            return (
              <li key={report.id}>
                <button
                  type="button"
                  className={`flex min-h-11 w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted/50 ${
                    active ? 'bg-muted/40 font-medium' : ''
                  }`}
                  onClick={() => onOpen(report)}
                >
                  <span>{reportTitle(report)}</span>
                  <span className="text-muted-foreground">
                    {report.status === 'draft' ? 'Entwurf' : 'Abgeschickt'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-medium">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap">{value}</dd>
    </div>
  );
}

async function downloadReportPdf(instanceId: string, reportId: string) {
  try {
    const response = await fetch(`/api/intranet/modules/${instanceId}/reports/${reportId}/pdf`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('PDF konnte nicht geladen werden.');
    }
    const blob = await response.blob();
    const header = response.headers.get('Content-Disposition') ?? '';
    const match = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(header);
    const filename = decodeURIComponent(match?.[1] ?? match?.[2] ?? 'Bautagesbericht.pdf');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'PDF herunterladen fehlgeschlagen.');
  }
}
